import { ComponentType } from '@angular/cdk/overlay';
import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel } from '@angular/material/select';
import { GetAbcXyzAnalysisDto } from '@api-types/dtos/get-abc-xyz-analysis.dto';
import { Measure } from '@api-types/enums/measure.enum';
import { H3Component, PmButtonComponent, PmTooltipDirective } from '@libs/ui';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartOptionsBuilder } from 'demand-forecasting/components/chart/chart-options-builder';
import { InsightsHttpClient } from 'demand-forecasting/services/http/insights-http-client';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import { selectForecastVersionByQueryParam } from 'demand-forecasting/store/forecast-version/forecast-version.selectors';
import {
  BaseChartOptions,
  HighchartsOptions,
} from 'demand-forecasting/types/forecast-chart/highcharts-options';
import Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { LucideScan } from '@lucide/angular';
import { finalize } from 'rxjs';
import { DefaultSelectionAbcXyzAnalysisWidget } from '../../types/types';
import {
  WidgetActionsDirective,
  WidgetBaseComponent,
  WidgetContentDirective,
  WidgetHeaderDirective,
  WidgetTitleDirective,
} from '../widget-base/widget-base.component';

interface AbcXyzAnalysisWidgetPointOptions
  extends Highcharts.PointOptionsObject {
  totalAmount: number;
  forecastAccuracy: number;
}

@Component({
  selector: 'pm-abc-xyz-analysis-widget',
  templateUrl: './abc-xyz-analysis-widget.component.html',
  imports: [
    MatCardModule,
    MatLabel,
    MatFormField,
    TranslateModule,
    WidgetBaseComponent,
    WidgetTitleDirective,
    WidgetHeaderDirective,
    WidgetActionsDirective,
    WidgetContentDirective,
    PmTooltipDirective,
    HighchartsChartComponent,
    FormsModule,
    MatInput,
    H3Component,
    PmButtonComponent,
  ],
})
export class AbcXyzAnalysisWidgetComponent
  extends WidgetBaseComponent<AbcXyzAnalysisWidgetComponent>
  implements OnInit
{
  @Input({
    required: true,
  })
  planningPeriod: number;

  @Input({
    required: true,
  })
  errorMeasure: Measure;

  @Input({
    required: false,
  })
  groups: string[];

  // Example: [0.8, 0.95]
  // A includes items that account to 80% of total relative volume, B = 15% and C = 5%
  @Input({
    required: true,
  })
  volumeBinLimits: number[];

  // Example:  [0.7, 0.3]
  // X includes items whose forecasts have an accuracy of 70% and above, Y includes items whose forecasts have an accuracy below 70% and above 30% and Z = lower than 30%
  @Input({
    required: true,
  })
  forecastAccuracyBinLimits: number[];

  // time period backwards from the latest actuals value
  @Input({
    required: false,
  })
  timePeriodBackwardsInMonths: number;

  @Input({
    required: false,
  })
  targetUnit?: string;
  private readonly store = inject(Store);
  selectedForecastVersion = this.store.selectSignal(
    selectForecastVersionByQueryParam,
  );

  selectedConfiguration = this.store.selectSignal(
    ConfigurationsSelectors.selectConfigurationByUrlParam,
  );

  protected readonly scanIcon = LucideScan;

  protected chartOptions = signal<HighchartsOptions>(
    BaseChartOptions.getEmptyChartOptions(),
  );

  isError = false;

  private readonly insightsHttpClient = inject(InsightsHttpClient);
  private readonly translateService = inject(TranslateService);
  public readonly data: DefaultSelectionAbcXyzAnalysisWidget = inject(
    MAT_DIALOG_DATA,
    {
      optional: true,
    },
  );

  ngOnInit(): void {
    if (this.data) {
      this.setupFullscreenView(this.data);
    }
    this.getAbcXyzAnalysisData();
  }

  getAbcXyzAnalysisData(): void {
    this.isLoading = true;
    this.isError = false;

    const forecastVersionId = this.selectedForecastVersion()?.id;
    const configurationId = this.selectedConfiguration()?.id;

    if (configurationId && forecastVersionId) {
      this.insightsHttpClient
        .getAbcXyzAnalysisData(
          configurationId,
          forecastVersionId,
          this.volumeBinLimits,
          this.forecastAccuracyBinLimits,
          this.planningPeriod ?? this.defaultPlanningPeriod,
          this.errorMeasure,
          this.timePeriodBackwardsInMonths,
          this.groups,
          this.targetUnit,
        )
        .pipe(
          finalize(() => {
            this.isLoading = false;
          }),
        )
        .subscribe({
          next: (data) => {
            this.createHeatmap(data);
          },
          error: () => {
            this.isError = true;
          },
        });
    }
  }

  createHeatmap(getAbcXyzAnalysisDto: GetAbcXyzAnalysisDto) {
    const heatmapData: AbcXyzAnalysisWidgetPointOptions[] =
      this.mapDtoToHeatmapData(getAbcXyzAnalysisDto);

    const heatmapChartOptions = this.getHeatmapChartOptions(heatmapData);
    this.chartOptions.set(heatmapChartOptions);
  }

  private mapDtoToHeatmapData(
    getAbcXyzAnalysisDto: GetAbcXyzAnalysisDto,
  ): AbcXyzAnalysisWidgetPointOptions[] {
    const heatmapData: AbcXyzAnalysisWidgetPointOptions[] = [];

    // A
    getAbcXyzAnalysisDto.aBins.forEach((binValue, index) => {
      heatmapData.push({
        x: index,
        y: 0,
        value: binValue.value,
        forecastAccuracy: binValue.forecastAccuracy,
        totalAmount: binValue.totalAmount,
      });
    });

    // B
    getAbcXyzAnalysisDto.bBins.forEach((binValue, index) => {
      heatmapData.push({
        x: index,
        y: 1,
        value: binValue.value,
        forecastAccuracy: binValue.forecastAccuracy,
        totalAmount: binValue.totalAmount,
      });
    });

    // C
    getAbcXyzAnalysisDto.cBins.forEach((binValue, index) => {
      heatmapData.push({
        x: index,
        y: 2,
        value: binValue.value,
        forecastAccuracy: binValue.forecastAccuracy,
        totalAmount: binValue.totalAmount,
      });
    });
    return heatmapData;
  }

  getHeatmapChartOptions(
    heatmapData: AbcXyzAnalysisWidgetPointOptions[],
  ): HighchartsOptions {
    // Translations for heatmap
    const translatedTooltipPrefix = this.translateService.instant(
      'RESULT.INSIGHTS.WIDGETS.ABC_XYZ_ANALYSIS.NUMBER_OF',
    );
    const translatedNoDataAvailable = this.translateService.instant(
      'UTILITY.PLACEHOLDER_NO_DATA_AVAILABLE',
    );
    const translatedGroups = this.translateService.instant(
      'RESULT.INSIGHTS.WIDGETS.ABC_XYZ_ANALYSIS.GROUPS',
    );
    const translatedRelativeVolume = this.translateService.instant(
      'RESULT.INSIGHTS.WIDGETS.ABC_XYZ_ANALYSIS.RELATIVE_VOLUME',
    );
    const translatedAverageAccuracy = this.translateService.instant(
      'RESULT.INSIGHTS.WIDGETS.ABC_XYZ_ANALYSIS.AVERAGE_ACCURACY',
    );
    const translatedForecastAccuracy = this.translateService.instant(
      'RESULT.INSIGHTS.WIDGETS.FORECAST_ACCURACY.TITLE',
    );
    const translatedShareOfTotalVolume = this.translateService.instant(
      'RESULT.INSIGHTS.WIDGETS.ABC_XYZ_ANALYSIS.SHARE_OF_TOTAL_VOLUME',
    );
    const binExplanationForTooltip = (bin: string): string =>
      this.getBinExplanationForTooltip(bin);

    const volumeBinLimitsMap = {
      A: `${this.volumeBinLimits[0] * 100}%`,
      B: `${((this.volumeBinLimits[1] - this.volumeBinLimits[0]) * 100).toFixed(0)}%`,
      C: `${((1 - this.volumeBinLimits[1]) * 100).toFixed(0)}%`,
    };

    const forecastAccuracyBinLimitsMap = {
      X: `&ge; ${this.forecastAccuracyBinLimits[0] * 100}%`,
      Y: `${this.forecastAccuracyBinLimits[0] * 100}% &gt; Y &ge; ${this.forecastAccuracyBinLimits[1] * 100}%`,
      Z: `&lt; ${this.forecastAccuracyBinLimits[1] * 100}%`,
    };

    return new ChartOptionsBuilder({
      series: [],
      xAxis: { title: { text: '' }, min: null, max: null },
      yAxis: { title: { text: '' } },
      navigator: {},
      rangeSelector: {},
      credits: {
        text: 'pacemaker.ai',
        href: 'https://pacemaker.ai/',
      },
      title: {
        text: '',
      },
      chart: {
        className:
          '[&_text]:!text-text [&_text]:!fill-text [&_text]:!font-body  [&>*]:!font-body',
      },
    })
      .withXAxis({
        categories: ['X', 'Y', 'Z'],
        labels: {
          useHTML: false,
          formatter() {
            const category = this.value;
            let forecastAccuracyBinLimits = '';
            if (
              typeof category === 'string' &&
              category in forecastAccuracyBinLimitsMap
            ) {
              forecastAccuracyBinLimits =
                forecastAccuracyBinLimitsMap[
                  category as keyof typeof forecastAccuracyBinLimitsMap
                ];
            }
            return `<tspan style="font-weight: bold">${category}</tspan><br>
                <span class="font-body text-xs">${forecastAccuracyBinLimits}</span>`;
          },
        },
        opposite: true,
        title: {
          text: `${translatedForecastAccuracy} (100% - ${this.errorMeasure.toUpperCase()})`,
          style: {
            fontSize: '16px',
          },
        },
        lineWidth: 1,
      })
      .withYAxis({
        categories: ['A', 'B', 'C'],
        labels: {
          useHTML: true,
          formatter() {
            const category = this.value;
            let volumeBinLimits = '';
            if (
              typeof category === 'string' &&
              category in volumeBinLimitsMap
            ) {
              volumeBinLimits =
                volumeBinLimitsMap[category as keyof typeof volumeBinLimitsMap]; // Safe access with narrowed type
            }
            return `<div class="font-body text-base text-center"><b>${category}</b></div>
                <span class="font-body text-xs">${volumeBinLimits}</span>`;
          },
        },
        reversed: true,
        title: {
          text: `${translatedShareOfTotalVolume} (${this.targetUnit ?? this.selectedConfiguration()?.startForecastConfig.target})`,
          style: {
            fontSize: '16px',
          },
        },
        lineWidth: 1,
      })
      .withSeries({
        type: 'heatmap',
        data: heatmapData,
        nullColor: 'transparent',
        dataLabels: {
          enabled: true,
          useHTML: false,
          style: {
            textOutline: 'none',
            fontSize: '14px',
            fontWeight: 'normal',
          },
          formatter() {
            const pointOptions = this
              .options as AbcXyzAnalysisWidgetPointOptions;
            const totalAmount = `${(pointOptions.totalAmount * 100).toFixed(2)}`;
            const forecastAccuracy = `${(pointOptions.forecastAccuracy * 100).toFixed(2)}`;

            return `<tspan style="font-size: 16px; font-weight: bold;">${pointOptions.value}</tspan> <tspan style="font-size: 16px;">${translatedGroups}</tspan> <br>
            ${translatedRelativeVolume}: <tspan style="font-weight: bold;">${totalAmount}%</tspan><br>
            ${translatedAverageAccuracy}: <tspan style="font-weight: bold;">${forecastAccuracy}%</tspan>`;
          },
        },
      })
      .withLegend({
        enabled: false,
        align: 'right',
        layout: 'vertical',
        margin: 0,
        verticalAlign: 'top',
        y: 25,
        symbolHeight: 500,
      })
      .withTooltip({
        outside: true,
        formatter() {
          const xCategory = this.series.xAxis.categories[this.x];
          const yCategory = this.y ? this.series.yAxis.categories[this.y] : 'A';
          const label = binExplanationForTooltip(yCategory + xCategory);
          const value =
            this.value === null ? translatedNoDataAvailable : this.value;
          return `${translatedTooltipPrefix} ${label}: <b>${value}</b>`;
        },
      })
      .withColorAxis({
        minColor: '#99D9FF',
        maxColor: '#023169',
      })
      .withExportingOptions({
        enabled: false,
      })
      .build();
  }

  getBinExplanationForTooltip(bin: string): string {
    const translatedWith = this.translateService.instant(
      'RESULT.INSIGHTS.WIDGETS.ABC_XYZ_ANALYSIS.WITH_AND',
    );

    const translatedHighVolumeItems = this.translateService.instant(
      'RESULT.INSIGHTS.WIDGETS.ABC_XYZ_ANALYSIS.HIGH_VOLUME_ITEMS',
    );
    const translatedMediumVolumeItems = this.translateService.instant(
      'RESULT.INSIGHTS.WIDGETS.ABC_XYZ_ANALYSIS.MEDIUM_VOLUME_ITEMS',
    );
    const translatedLowVolumeItems = this.translateService.instant(
      'RESULT.INSIGHTS.WIDGETS.ABC_XYZ_ANALYSIS.LOW_VOLUME_ITEMS',
    );

    const translatedHighForecastAccuracy = this.translateService.instant(
      'RESULT.INSIGHTS.WIDGETS.ABC_XYZ_ANALYSIS.HIGH_FORECAST_ACCURACY',
    );
    const translatedModerateForecastAccuracy = this.translateService.instant(
      'RESULT.INSIGHTS.WIDGETS.ABC_XYZ_ANALYSIS.MODERATE_FORECAST_ACCURACY',
    );
    const translatedLowForecastAccuracy = this.translateService.instant(
      'RESULT.INSIGHTS.WIDGETS.ABC_XYZ_ANALYSIS.LOW_FORECAST_ACCURACY',
    );

    if (bin.includes('A') && bin.includes('X')) {
      return `<b>${translatedHighVolumeItems}</b> ${translatedWith} <b>${translatedHighForecastAccuracy}</b>`;
    }
    if (bin.includes('A') && bin.includes('Y')) {
      return `<b>${translatedHighVolumeItems}</b> ${translatedWith} <b>${translatedModerateForecastAccuracy}</b>`;
    }
    if (bin.includes('A') && bin.includes('Z')) {
      return `<b>${translatedHighVolumeItems}</b> ${translatedWith} <b>${translatedLowForecastAccuracy}</b>`;
    }
    if (bin.includes('B') && bin.includes('X')) {
      return `<b>${translatedMediumVolumeItems}</b> ${translatedWith} <b>${translatedHighForecastAccuracy}</b>`;
    }
    if (bin.includes('B') && bin.includes('Y')) {
      return `<b>${translatedMediumVolumeItems}</b> ${translatedWith} <b>${translatedModerateForecastAccuracy}</b>`;
    }
    if (bin.includes('B') && bin.includes('Z')) {
      return `<b>${translatedMediumVolumeItems}</b> ${translatedWith} <b>${translatedLowForecastAccuracy}</b>`;
    }
    if (bin.includes('C') && bin.includes('X')) {
      return `<b>${translatedLowVolumeItems}</b> ${translatedWith} <b>${translatedHighForecastAccuracy}</b>`;
    }
    if (bin.includes('C') && bin.includes('Y')) {
      return `<b>${translatedLowVolumeItems}</b> ${translatedWith} <b>${translatedModerateForecastAccuracy}</b>`;
    }
    if (bin.includes('C') && bin.includes('Z')) {
      return `<b>${translatedLowVolumeItems}</b> ${translatedWith} <b>${translatedLowForecastAccuracy}</b>`;
    }
    return '';
  }

  // Override the showFullScreen method but afterClosed is not needed in this widget
  showFullScreen(
    componentType: ComponentType<AbcXyzAnalysisWidgetComponent>,
    filterSelection?: DefaultSelectionAbcXyzAnalysisWidget,
  ): MatDialogRef<AbcXyzAnalysisWidgetComponent> | null {
    super.showFullScreen(componentType, filterSelection);
    return null;
  }

  private setupFullscreenView(data: DefaultSelectionAbcXyzAnalysisWidget) {
    this.volumeBinLimits = data.volumeBinLimits;
    this.forecastAccuracyBinLimits = data.forecastAccuracyBinLimits;
    this.groups = data.groups;
    this.planningPeriod = data.planningPeriod;
    this.targetUnit = data.targetUnit;
    this.errorMeasure = data.errorMeasure;
    this.timePeriodBackwardsInMonths = data.timePeriodBackwardsInMonths;
  }

  get defaultPlanningPeriod() {
    return 1;
  }

  get AbcXyzAnalysisWidgetComponent(): typeof AbcXyzAnalysisWidgetComponent {
    return AbcXyzAnalysisWidgetComponent;
  }
}
